// EF-193: pull SetupStagingPanel.handleSendOrders' bracket-building loop out
// into a pure helper so WorkingSetupCard.Edit can reuse the same logic.
// Behaviour mirrors the panel verbatim (lines 378–428 in the pre-EF-193
// SetupStagingPanel.jsx): partial-target sizing at floor(shares/3), the
// >=3 share take-profit threshold, and split-bracket emission.

import { buildSplitBracketOrders } from './bracketOrderGroups.js'

/**
 * Build OMS bracket payloads + UI summaries for a setup placement.
 *
 * @param {object} params
 * @param {string} params.symbol
 * @param {string} params.entryType  'On Strength' | 'Pullback'
 * @param {number|string} params.triggerPrice
 * @param {number|string} params.stopPrice
 * @param {number|string} params.targetPrice
 * @param {Array}  params.accountSizing  [{ account_number, shares, dollarRisk }]
 * @param {Set}    params.selectedAccountNumbers
 * @param {(acct: string) => string|null} params.getNickname
 * @returns {{
 *   orderSummaries: Array,
 *   gatewayOrders: Array,
 *   splitMetaByAccount: Record<string, {splitBracket: boolean, bracketGroupId: string|null}>,
 * }}
 */
export function buildSetupOrderRequests({
  symbol,
  entryType,
  triggerPrice,
  stopPrice,
  targetPrice,
  accountSizing,
  selectedAccountNumbers,
  getNickname,
}) {
  const trigger = Number(triggerPrice)
  const stop = Number(stopPrice)
  const target = Number(targetPrice)
  const sel = selectedAccountNumbers instanceof Set ? selectedAccountNumbers : new Set(selectedAccountNumbers || [])

  const activeSizing = (accountSizing || []).filter(
    a => sel.has(a.account_number) && Number(a.shares) > 0
  )

  const orderSummaries = []
  const gatewayOrders = []
  const splitMetaByAccount = {}

  for (const acct of activeSizing) {
    // EF-182: small positions (1 or 2 shares per account) ride the stop with
    // no take-profit leg. For ≥3 shares, keep the 1/3 partial-TP.
    const shares = Number(acct.shares) || 0
    const useTakeProfit = shares >= 3 && Number.isFinite(target) && target > 0
    const sellQty = useTakeProfit ? Math.floor(shares / 3) : 0

    const splitOrders = buildSplitBracketOrders({
      accountNumber: acct.account_number,
      symbol: (symbol || '').toUpperCase(),
      quantity: shares,
      entryPrice: trigger,
      stopPrice: stop,
      targetPrice: useTakeProfit ? target : null,
      targetQuantity: useTakeProfit ? sellQty : 0,
      entryType,
      duration: 'GOOD_TILL_CANCEL',
      session: 'NORMAL',
      assetType: 'EQUITY',
    })
    const splitBracket = splitOrders.length > 1
    const bracketGroupId = splitBracket ? splitOrders[0].bracketGroupId : null
    splitMetaByAccount[acct.account_number] = { splitBracket, bracketGroupId }

    const nickname = typeof getNickname === 'function' ? getNickname(acct.account_number) : null

    orderSummaries.push({
      accountName: nickname || `****${String(acct.account_number || '').slice(-4)}`,
      accountNumber: acct.account_number,
      quantity: shares,
      runnerQty: shares - sellQty,
      side: 'BUY',
      // EF-118: entryType drives the parent leg type. On Strength → STOP
      // (buy when price rises to trigger), Pullback → LIMIT (buy at trigger
      // or better when price dips).
      orderType: entryType === 'On Strength' ? 'STOP' : 'LIMIT',
      limitPrice: trigger,
      stopPrice: stop,
      targetPrice: useTakeProfit ? target : null,
      sellQty,
      entryType,
      splitBracket,
      bracketGroupId,
    })
    gatewayOrders.push(...splitOrders)
  }

  return { orderSummaries, gatewayOrders, splitMetaByAccount }
}
